<template>
  <div class="tc-preview-panel" @keydown.escape="closePopover">
    <div class="tc-preview-header">
      <span class="tc-preview-title">Timesheet Preview</span>
      <button
        v-if="showEventsToggle"
        type="button"
        class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
        :class="{ active: eventsOpen }"
        :aria-pressed="eventsOpen"
        aria-label="Toggle events inspector"
        @click="emit('toggleEvents')"
      >
        <svg viewBox="0 0 18 18" fill="none" aria-hidden="true" style="width:14px;height:14px;flex-shrink:0">
          <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        Events
      </button>
    </div>

    <div class="tc-preview-scroll tc-scroll">
      <table class="tc-preview-table" role="grid" aria-label="Draft-first timesheet grid">
        <thead>
          <tr>
            <th class="tc-preview-col-project" scope="col">Project</th>
            <th
              v-for="day in DAYS"
              :key="day.key"
              scope="col"
              class="tc-preview-col-day"
              :class="{ 'tc-preview-col-day--weekend': day.weekend }"
            >
              <div class="tc-preview-col-day-dow">{{ day.dow[0] }}</div>
              <div class="tc-preview-col-day-num">{{ day.date }}</div>
            </th>
            <th scope="col" class="tc-preview-col-total">Σ</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in ROWS"
            :key="row.wbs"
            :class="[
              row.existing ? 'tc-preview-row-existing' : 'tc-preview-row-draft',
              highlightWbs === row.wbs ? 'tc-preview-row--highlighted' : '',
            ]"
            :style="!row.existing ? 'cursor:pointer' : ''"
            @click="!row.existing && emit('rowClick', row.wbs)"
          >
            <td
              class="tc-preview-wbs-cell"
              :class="row.existing ? 'tc-preview-wbs-cell--existing' : 'tc-preview-wbs-cell--draft'"
            >
              <div class="tc-preview-wbs-code txt-mono">{{ row.wbs }}</div>
              <div class="tc-preview-wbs-name">{{ row.name }}</div>
              <div v-if="row.client" class="tc-preview-wbs-client">{{ row.client }}</div>
            </td>

            <td
              v-for="day in DAYS"
              :key="day.key"
              class="tc-preview-day-cell"
              :class="[
                day.weekend ? 'tc-preview-day-cell--weekend' : '',
                row.hours[day.key] ? 'tc-preview-day-cell--value' : 'tc-preview-day-cell--empty',
                isUncertain(row, day.key) ? 'tc-preview-day-cell--uncertain' : '',
              ]"
              :aria-label="isUncertain(row, day.key) ? `Uncertain attribution for ${row.wbs} on ${day.dow} — click for reasoning` : undefined"
              @click="isUncertain(row, day.key) ? onUncertainClick($event, row.wbs, day.key) : null"
            >
              <template v-if="row.hours[day.key]">{{ row.hours[day.key]!.toFixed(1) }}</template>
            </td>

            <td class="tc-preview-total-cell">{{ rowTotal(row) }}</td>
          </tr>
        </tbody>

        <tfoot>
          <tr class="tc-preview-footer-row">
            <td class="tc-preview-footer-label">Daily total</td>
            <td
              v-for="day in DAYS"
              :key="day.key"
              class="tc-preview-footer-day"
              :class="[
                day.weekend ? 'tc-preview-footer-day--weekend' : '',
                footerClass(day),
              ]"
            >{{ dayTotal(day.key) || '' }}</td>
            <td class="tc-preview-footer-total">{{ grandTotal }}h</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <Teleport to="body">
      <div
        v-if="openBadge"
        class="tc-cell-popover-backdrop"
        @click="closePopover"
      />
      <div
        v-if="openBadge"
        class="tc-cell-popover"
        role="dialog"
        aria-modal="true"
      >
        <DfReasoningContent
          v-if="popoverMode === 'reasoning'"
          :context-label="`${openBadge.wbs} · Support · ${dayLabel(openBadge.day)}`"
          :closeable="true"
          @close="closePopover"
          @change-project="popoverMode = 'picker'"
        />
        <DfPopoverContent
          v-else
          :context-label="`${openBadge.wbs} · Support · ${dayLabel(openBadge.day)}`"
          :closeable="true"
          @close="closePopover"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import DfPopoverContent from './DfPopoverContent.vue';
import DfReasoningContent from './DfReasoningContent.vue';

interface GridRow {
  wbs: string;
  name: string;
  client?: string;
  existing?: boolean;
  hours: Record<string, number | null>;
  badge?: { day: string };
}

interface DayDef {
  key: string;
  dow: string;
  date: string;
  weekend: boolean;
  standard: number;
}

interface BadgeTarget {
  wbs: string;
  day: string;
}

const { highlightWbs, showEventsToggle = false, eventsOpen = false } = defineProps<{
  highlightWbs?: string;
  showEventsToggle?: boolean;
  eventsOpen?: boolean;
}>();

const emit = defineEmits<{
  rowClick: [wbs: string];
  toggleEvents: [];
}>();

const openBadge = ref<BadgeTarget | null>(null);
const popoverMode = ref<'reasoning' | 'picker'>('reasoning');

const DAYS: DayDef[] = [
  { key: 'mon', dow: 'Mon', date: '16', weekend: false, standard: 8 },
  { key: 'tue', dow: 'Tue', date: '17', weekend: false, standard: 8 },
  { key: 'wed', dow: 'Wed', date: '18', weekend: false, standard: 8 },
  { key: 'thu', dow: 'Thu', date: '19', weekend: false, standard: 8 },
  { key: 'fri', dow: 'Fri', date: '20', weekend: false, standard: 8 },
  { key: 'sat', dow: 'Sat', date: '21', weekend: true, standard: 0 },
  { key: 'sun', dow: 'Sun', date: '22', weekend: true, standard: 0 },
];

const ROWS: GridRow[] = [
  {
    wbs: '2024-ACME-001',
    name: 'Support',
    client: 'Acme Corp',
    hours: { mon: 1.0, tue: 1.5, wed: null, thu: 0.5, fri: null, sat: null, sun: null },
    badge: { day: 'tue' },
  },
  {
    wbs: '2024-NW-002',
    name: 'Concept',
    client: 'Northwind',
    hours: { mon: 0.5, tue: 2.0, wed: 1.0, thu: null, fri: null, sat: null, sun: null },
  },
  {
    wbs: 'OH-MTG',
    name: 'Internal meetings',
    client: 'Overhead',
    hours: { mon: 0.5, tue: null, wed: 0.5, thu: 0.5, fri: null, sat: null, sun: null },
  },
  {
    wbs: '2024-OH-PROD',
    name: 'Admin',
    client: 'Overhead',
    existing: true,
    hours: { mon: null, tue: null, wed: null, thu: null, fri: 1.0, sat: null, sun: null },
  },
];

function dayLabel(key: string): string {
  const d = DAYS.find(d => d.key === key);
  return d ? `${d.dow} Apr ${d.date} · 1.5h` : '';
}

function isUncertain(row: GridRow, dayKey: string): boolean {
  return !!(row.badge && row.badge.day === dayKey && row.hours[dayKey]);
}

function onUncertainClick(ev: MouseEvent, wbs: string, day: string) {
  ev.stopPropagation();
  if (openBadge.value?.wbs === wbs && openBadge.value?.day === day) {
    closePopover();
  } else {
    openBadge.value = { wbs, day };
    popoverMode.value = 'reasoning';
  }
}

function closePopover() {
  openBadge.value = null;
  popoverMode.value = 'reasoning';
}

function rowTotal(row: GridRow): string {
  const sum = Object.values(row.hours).reduce<number>((acc, v) => acc + (v ?? 0), 0);
  return sum ? sum.toFixed(1) : '–';
}

function dayTotal(key: string): string {
  const sum = ROWS.reduce<number>((acc, r) => acc + (r.hours[key] ?? 0), 0);
  return sum ? sum.toFixed(1) : '';
}

const grandTotal = computed<string>(() => {
  const sum = ROWS.reduce<number>((acc, r) =>
    acc + Object.values(r.hours).reduce<number>((a, v) => a + (v ?? 0), 0), 0);
  return sum ? sum.toFixed(1) : '–';
});

function footerClass(day: DayDef): string {
  if (day.weekend) return 'tc-preview-footer-day--weekend';
  const total = ROWS.reduce<number>((acc, r) => acc + (r.hours[day.key] ?? 0), 0);
  if (total === 0) return 'tc-preview-footer-day--empty';
  if (total > day.standard) return 'tc-preview-footer-day--over';
  if (total >= day.standard) return 'tc-preview-footer-day--enough';
  return 'tc-preview-footer-day--partial';
}
</script>
